import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookOpen, Download, Loader2, Sparkles, ArrowRight, Compass } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EbookModal = ({ open, onClose, onCheckoutDiscount }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form"); // form | success | offer
  const [downloadUrl, setDownloadUrl] = useState("");
  const [discountOffer, setDiscountOffer] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ebook/request`, { email, name });
      setDownloadUrl(res.data.download_url);
      setDiscountOffer(res.data.discount_offer);
      setStep("success");
      toast.success("Your ebook is ready!");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep("form");
    setEmail("");
    setName("");
    setDownloadUrl("");
    setDiscountOffer(null);
    onClose();
  };

  const handleClaimOffer = () => {
    if (discountOffer && onCheckoutDiscount) {
      onCheckoutDiscount({
        id: discountOffer.product_id,
        name: discountOffer.product_name,
        price: discountOffer.discount_price,
        original_price: discountOffer.original_price,
        type: "course",
        use_discount: true,
      });
    }
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid="ebook-modal"
        className="sm:max-w-md rounded-2xl border"
        style={{ borderColor: "var(--ll-border)", background: "var(--ll-bg)" }}
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-medium" style={{ color: "var(--ll-text)" }}>
            {step === "form" && "Download Free Ebook"}
            {step === "success" && "Your Ebook is Ready!"}
            {step === "offer" && "Exclusive Offer For You"}
          </DialogTitle>
          <DialogDescription className="sr-only">Download your free Burnout To Balance ebook</DialogDescription>
        </DialogHeader>

        {/* Step 1: Email Form */}
        {step === "form" && (
          <form onSubmit={handleSubmit} className="space-y-5 pt-2">
            <div className="flex items-center gap-3 p-4 rounded-xl" style={{ background: "var(--ll-bg-secondary)" }}>
              <BookOpen size={20} style={{ color: "var(--ll-green)" }} />
              <div>
                <p className="text-sm font-medium" style={{ color: "var(--ll-text)", fontFamily: "'Outfit', sans-serif" }}>
                  Burnout To Balance
                </p>
                <p className="text-xs" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
                  Free comprehensive wellness guide — 8 chapters
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ebook-name" className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
                Name (optional)
              </Label>
              <Input
                id="ebook-name"
                data-testid="ebook-name-input"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-xl border bg-white/50 focus:bg-white"
                style={{ borderColor: "var(--ll-border)" }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ebook-email" className="text-sm" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
                Email *
              </Label>
              <Input
                id="ebook-email"
                data-testid="ebook-email-input"
                type="email"
                placeholder="you@example.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-xl border bg-white/50 focus:bg-white"
                style={{ borderColor: "var(--ll-border)" }}
              />
            </div>

            <button
              type="submit"
              data-testid="ebook-submit-btn"
              disabled={loading}
              className="w-full rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              {loading ? "Processing..." : "Get Your Free Copy"}
            </button>

            <p className="text-xs text-center" style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}>
              We respect your privacy. No spam, ever.
            </p>
          </form>
        )}

        {/* Step 2: Success + Download */}
        {step === "success" && (
          <div className="py-4 space-y-5">
            <div
              className="w-16 h-16 rounded-full mx-auto flex items-center justify-center"
              style={{ background: "var(--ll-green)" }}
            >
              <Download size={28} style={{ color: "var(--ll-text)" }} />
            </div>
            <p className="text-sm text-center" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
              Your copy of <strong>Burnout To Balance</strong> is ready to download.
            </p>

            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ebook-download-link"
              className="w-full rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2"
              style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}
            >
              <Download size={16} />
              Download Your Ebook (PDF)
            </a>

            {discountOffer && (
              <button
                data-testid="ebook-see-offer-btn"
                onClick={() => setStep("offer")}
                className="w-full rounded-full px-6 py-3 text-sm font-medium border transition-all duration-300 hover:-translate-y-0.5 flex items-center justify-center gap-2"
                style={{
                  borderColor: "var(--ll-accent)",
                  color: "var(--ll-text-secondary)",
                  background: "rgba(247, 208, 70, 0.1)",
                  fontFamily: "'Outfit', sans-serif",
                }}
              >
                <Sparkles size={16} style={{ color: "var(--ll-accent)" }} />
                We have a special offer for you
                <ArrowRight size={14} />
              </button>
            )}

            <button
              data-testid="ebook-close-success"
              onClick={handleClose}
              className="w-full text-xs text-center py-2 transition-colors hover:opacity-70"
              style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}
            >
              No thanks, close
            </button>

            {/* Gentle resources nudge */}
            <Link
              to="/resources"
              onClick={handleClose}
              data-testid="ebook-resources-link"
              className="w-full flex items-center justify-center gap-1.5 text-[11px] py-2 transition-colors hover:opacity-70"
              style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}
            >
              <Compass size={11} />
              Browse curated EQ resources
            </Link>
          </div>
        )}

        {/* Step 3: Lead Magnet Discount Offer */}
        {step === "offer" && discountOffer && (
          <div className="py-4 space-y-5">
            <div
              className="p-5 rounded-xl text-center relative overflow-hidden"
              style={{ background: "linear-gradient(135deg, rgba(247,208,70,0.15), rgba(163,184,153,0.15))" }}
            >
              <p
                className="text-xs tracking-[0.2em] uppercase mb-2"
                style={{ color: "var(--ll-accent-hover)", fontFamily: "'Outfit', sans-serif" }}
              >
                Exclusive Ebook Reader Offer
              </p>
              <h3 className="font-serif text-xl font-medium mb-1" style={{ color: "var(--ll-text)" }}>
                {discountOffer.product_name}
              </h3>
              <div className="flex items-center justify-center gap-3 mb-3">
                <span
                  className="text-sm line-through"
                  style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}
                >
                  R{discountOffer.original_price?.toLocaleString()}
                </span>
                <span className="font-serif text-3xl font-semibold" style={{ color: "var(--ll-text)" }}>
                  R{discountOffer.discount_price?.toLocaleString()}
                </span>
              </div>
              <p
                className="text-xs px-3 py-1 rounded-full inline-block"
                style={{
                  background: "var(--ll-accent)",
                  color: "var(--ll-text)",
                  fontFamily: "'Outfit', sans-serif",
                }}
              >
                Save R{discountOffer.savings?.toLocaleString()}
              </p>
            </div>

            <p className="text-sm text-center" style={{ color: "var(--ll-text-secondary)", fontFamily: "'Outfit', sans-serif" }}>
              Ready to go deeper? The full course gives you 10 video lessons, worksheets, community access, and a certificate of completion.
            </p>

            <button
              data-testid="ebook-claim-discount-btn"
              onClick={handleClaimOffer}
              className="w-full rounded-full px-6 py-3.5 text-sm font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2"
              style={{ background: "var(--ll-cta)", color: "var(--ll-cta-text)", fontFamily: "'Outfit', sans-serif" }}
            >
              <Sparkles size={16} />
              Claim Your Discount — R{discountOffer.discount_price?.toLocaleString()}
            </button>

            <button
              data-testid="ebook-back-to-download"
              onClick={() => setStep("success")}
              className="w-full text-xs text-center py-2 transition-colors hover:opacity-70"
              style={{ color: "var(--ll-text-muted)", fontFamily: "'Outfit', sans-serif" }}
            >
              Back to download
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EbookModal;
